/** Matches LabelMaster.Core.Config.PublicKeys.AzureAppInsightsKey */
export const AZURE_APP_INSIGHTS_KEY = "7dec69f6-c8d3-4017-b8d5-0a608853c729";

const INGEST_URL = "https://dc.services.visualstudio.com/v2/track";

export type TelemetryProperties = Record<string, string | number | boolean>;

function toStringProperties(
  properties?: TelemetryProperties,
): Record<string, string> {
  if (!properties) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    result[key] = String(value);
  }
  return result;
}

/**
 * Lightweight Application Insights event sender (no SDK dependency).
 * Skips network when disabled (local mock / debug).
 */
export class AppInsightsClient {
  private readonly instrumentationKey: string;
  private enabled: boolean;
  private sessionId: string;
  private userId?: string;

  constructor(instrumentationKey: string, enabled: boolean) {
    this.instrumentationKey = instrumentationKey;
    this.enabled = enabled;
    this.sessionId = cryptoRandomId();
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public setAuthenticatedUser(userId: string | undefined): void {
    this.userId = userId;
  }

  public trackEvent(name: string, properties?: TelemetryProperties): void {
    if (!this.enabled || !this.instrumentationKey) {
      return;
    }

    const payload = [
      {
        name: "Microsoft.ApplicationInsights.Event",
        time: new Date().toISOString(),
        iKey: this.instrumentationKey,
        tags: {
          "ai.session.id": this.sessionId,
          "ai.cloud.role": "LabelMaster.Web",
          ...(this.userId ? { "ai.user.id": this.userId } : {}),
        },
        data: {
          baseType: "EventData",
          baseData: {
            ver: 2,
            name,
            properties: toStringProperties(properties),
          },
        },
      },
    ];

    const body = JSON.stringify(payload);
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(INGEST_URL, blob)) {
          return;
        }
      }
      void fetch(INGEST_URL, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        mode: "cors",
      }).catch(() => {
        // Best-effort telemetry
      });
    } catch {
      // Best-effort telemetry
    }
  }

  public trackPageView(route: string): void {
    this.trackEvent("WebPageView", { route });
  }
}

function cryptoRandomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

let sharedClient: AppInsightsClient | null = null;

export function getAppInsightsClient(): AppInsightsClient {
  if (!sharedClient) {
    sharedClient = new AppInsightsClient(AZURE_APP_INSIGHTS_KEY, false);
  }
  return sharedClient;
}
